/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { forwardRef } from "react";

import { spacing } from "../../../../../global/emotion";
import { CAROUSEL_GRID_SIZES } from "../../../../constants/carouselGridSizes.constants";
import type { CarouselApi } from "../../../../hooks/useCarousel";
import type { CarouselGridSize } from "../../../../types/carouselGridSize.types";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";

const cssMediaMinWidth1 = "@media (min-width: 640px) and (max-width: 767px)";
const cssMediaMinWidth2 = "@media (min-width: 768px) and (max-width: 1023px)";
const cssMediaMinWidth3 = "@media (min-width: 1024px) and (max-width: 1263px)";
const cssMediaMinWidth4 = "@media (min-width: 1264px) and (max-width: 1535px)";
const cssMediaMinWidth5 = "@media (min-width: 1536px)";

const hallowElement: HallowElement = "div";

const determinedElementStyle: Interpolation = {
  display: "grid",
  gap: spacing.s,
  [cssMediaMinWidth2]: { gap: spacing.m },
};
const determinedElementXLStyle: Interpolation = {
  gap: spacing.xl,
  [cssMediaMinWidth2]: { gap: spacing.xl },
};

const gridTemplateColumnsStyle: Interpolation<{ size: CarouselGridSize }> = (
  props,
) => ({
  gridTemplateColumns: `repeat(${props.size[0]}, minmax(0, 1fr))`,
  [cssMediaMinWidth1]: {
    gridTemplateColumns: `repeat(${props.size[1]}, minmax(0, 1fr))`,
  },
  [cssMediaMinWidth2]: {
    gridTemplateColumns: `repeat(${props.size[2]}, minmax(0, 1fr))`,
  },
  [cssMediaMinWidth3]: {
    gridTemplateColumns: `repeat(${props.size[3]}, minmax(0, 1fr))`,
  },
  [cssMediaMinWidth4]: {
    gridTemplateColumns: `repeat(${props.size[4]}, minmax(0, 1fr))`,
  },
  [cssMediaMinWidth5]: {
    gridTemplateColumns: `repeat(${props.size[5]}, minmax(0, 1fr))`,
  },
});

export type GridProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  size?: CarouselApi["size"];
};

export const Grid = forwardRef<any, GridProps>(
  ({ asChild = false, children, size = "m", ...props }, ref) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });
    const determinedCarouselGridSize =
      CAROUSEL_GRID_SIZES[size as keyof typeof CAROUSEL_GRID_SIZES] ??
      (size as CarouselGridSize);

    return (
      <DeterminedElement
        ref={ref}
        css={[
          determinedElementStyle,
          size === "xl" && determinedElementXLStyle,
          gridTemplateColumnsStyle({ size: determinedCarouselGridSize }),
        ]}
        {...props}
      >
        {children}
      </DeterminedElement>
    );
  },
);

Grid.displayName = "Grid";
